<template>
  <div>
    <button class="feedback" @click="open = !open">
      <img src="@/assets/icons/social-media/feedback-icon.svg" alt="feedback">
    </button>

    <div v-if="open" class="chat">
      <button class="close" @click="close">×</button>

      <template v-if="!sent">
        <div class="chat-header">
          <div class="chat-avatar">
            <img src="@/assets/icons/social-media/feedback-icon.svg" alt="feedback">
          </div>
          <div>
            <h3>Здравствуйте!</h3>
            <span>Служба обратной связи</span>
          </div>
        </div>

        <div class="message">
          Вы обратились в службу обратной связи Примгидромета.
          <br><br>
          Здесь вы можете:
          <br>
          — сообщить о неточностях в прогнозе
          <br>
          — поделиться наблюдениями
          <br>
          — оставить отзыв о работе приложения
          <br><br>
          Просто напишите ваше сообщение — оно будет учтено в нашей работе.
        </div>

        <textarea v-model="message" placeholder="Ваше сообщение..." />

        <div class="attachments">
          <input
            ref="imageInput"
            class="attachments__input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            @change="handleImageSelection"
          >

          <button
            class="attachments__button"
            type="button"
            :disabled="images.length >= maxImages || isSending"
            @click="$refs.imageInput.click()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19.5 12.6l-7.8 7.8a5 5 0 01-7.1-7.1l8.5-8.5a3.5 3.5 0 015 5l-8.5 8.5a2 2 0 01-2.8-2.8l7.8-7.8" />
            </svg>
            Добавить изображение
          </button>

          <span class="attachments__limit">
            до {{ maxImages }} файлов
          </span>
        </div>

        <div v-if="images.length" class="attachment-previews">
          <div
            v-for="(image, index) in images"
            :key="image.id"
            class="attachment-preview"
          >
            <img :src="image.previewUrl" :alt="image.file.name">
            <button
              type="button"
              :aria-label="`Удалить ${image.file.name}`"
              :disabled="isSending"
              @click="removeImage(index)"
            >
              ×
            </button>
          </div>
        </div>

        <div v-if="attachmentError" class="form-error">
          {{ attachmentError }}
        </div>

        <div v-if="sendError" class="form-error">
          {{ sendError }}
        </div>

        <button
          class="send"
          :disabled="(!message.trim() && !images.length) || isSending"
          @click="send"
        >
          {{ isSending ? "Отправляем…" : "Отправить" }}
        </button>
      </template>

      <template v-else>
        <div class="chat-header">
          <div class="chat-avatar">✓</div>
          <div>
            <h3>Спасибо!</h3>
            <span>Сообщение получено</span>
          </div>
        </div>

        <div class="message">
          Спасибо за ваше сообщение!
          <br><br>
          Мы получили вашу обратную связь и обязательно учтём её при улучшении сервиса.
        </div>

        <button class="send" @click="close">
          Закрыть
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import c from "@/store/modules/start-page/consts";

export default {
  name: "FeedbackCircle",

  data() {
    return {
      open: false,
      sent: false,
      message: "",
      isSending: false,
      images: [],
      maxImages: 3,
      maxImageSize: 5 * 1024 * 1024,
      attachmentError: "",
      sendError: "",
    };
  },

  methods: {
    ...mapActions(c.STORE_PATH, [
      c.SEND_FEEDBACK,
    ]),

    async send() {
      if ((!this.message.trim() && !this.images.length) || this.isSending) {
        return;
      }

      this.isSending = true;
      this.sendError = "";

      try {
        await this[c.SEND_FEEDBACK]({
          feedbackText: this.message.trim(),
          images: this.images.map((image) => image.file),
        });

        this.sent = true;
        this.message = "";
        this.clearImages();
      } catch (e) {
        console.error(e);
        this.sendError = "Не удалось отправить сообщение. Попробуйте ещё раз.";
      } finally {
        this.isSending = false;
      }
    },

    handleImageSelection(event) {
      const selectedFiles = Array.from(event.target.files || []);
      const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

      this.attachmentError = "";

      for (const file of selectedFiles) {
        if (this.images.length >= this.maxImages) {
          this.attachmentError = `Можно прикрепить не больше ${this.maxImages} изображений.`;
          break;
        }

        if (!allowedTypes.includes(file.type)) {
          this.attachmentError = "Поддерживаются изображения JPG, PNG и WebP.";
          continue;
        }

        if (file.size > this.maxImageSize) {
          this.attachmentError = `Файл «${file.name}» больше 5 МБ.`;
          continue;
        }

        this.images.push({
          id: `${Date.now()}-${file.name}-${this.images.length}`,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      }

      event.target.value = "";
    },

    removeImage(index) {
      const [removedImage] = this.images.splice(index, 1);

      if (removedImage) {
        URL.revokeObjectURL(removedImage.previewUrl);
      }

      this.attachmentError = "";
    },

    clearImages() {
      this.images.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      this.images = [];
    },

    close() {
      this.open = false;
      this.sent = false;
      this.message = "";
      this.attachmentError = "";
      this.sendError = "";
      this.clearImages();
    },
  },

  beforeUnmount() {
    this.clearImages();
  },
};
</script>

<style scoped>
.feedback {
  position: fixed;
  right: 50px;
  bottom: 50px;
  width: 54px;
  height: 54px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0,0,0,.25);
  transition: .2s;
}

.feedback:hover {
  transform: scale(1.1);
}

.feedback img {
  width: 100%;
  height: 100%;
  display: block;
}

.chat {
  position: fixed;
  right: 50px;
  bottom: 120px;
  z-index: 10000;
  width: 340px;
  max-height: calc(100vh - 150px);
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 18px 50px rgba(0,0,0,.28);
}

.chat::after {
  content: "";
  position: absolute;
  right: 22px;
  bottom: -10px;
  border-width: 10px 10px 0;
  border-style: solid;
  border-color: #fff transparent transparent;
}

.close {
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 2;
  border: none;
  background: rgba(255,255,255,.25);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
}

.chat-header {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 18px 20px;
  background:
      linear-gradient(
          180deg,
          rgba(80, 117, 153, 1),
          rgba(50, 97, 123, 1)
      );
  //background: linear-gradient(135deg, #1e88e5, #42a5f5);
  color: #fff;
}

.chat-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(255,255,255,.2);
  box-shadow:
      0 8px 32px rgba(80,120,180,.12),
      inset 0 1px 0 rgba(255,255,255,.22);
  font-size: 22px;
  font-weight: 700;
}

.chat h3 {
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
}

.chat span {
  font-size: 13px;
  opacity: .9;
}

.message {
  margin: 16px;
  padding: 14px;
  border-radius: 16px;
  background: #f1f7ff;
  color: #1f2937;
  font-size: 14px;
  line-height: 1.5;
}

textarea {
  box-sizing: border-box;
  display: block;
  width: calc(100% - 32px);
  min-height: 95px;
  margin: 0 16px;
  padding: 12px;
  border: 1px solid #d7e3f3;
  border-radius: 16px;
  font-size: 14px;
  resize: vertical;
  outline: none;
}

textarea:focus {
  border-color: #1e88e5;
  box-shadow: 0 0 0 3px rgba(30,136,229,.12);
}

.attachments {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 16px 0;
}

.attachments__input {
  display: none;
}

.attachments__button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: 1px solid #c9d9e8;
  border-radius: 10px;
  background: #f7fafc;
  color: #32617b;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  transition: background-color .2s, border-color .2s;
}

.attachments__button:hover:not(:disabled) {
  border-color: #7e9fb4;
  background: #edf5f9;
}

.attachments__button:disabled {
  opacity: .55;
  cursor: not-allowed;
}

.attachments__button svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.attachments__limit {
  color: #7b8794;
  font-size: 11px;
}

.attachment-previews {
  display: flex;
  gap: 8px;
  margin: 10px 16px 0;
}

.attachment-preview {
  position: relative;
  width: 62px;
  height: 62px;
}

.attachment-preview img {
  width: 100%;
  height: 100%;
  border-radius: 11px;
  object-fit: cover;
  box-shadow: 0 2px 7px rgba(31, 41, 55, .16);
}

.attachment-preview button {
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0 0 2px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #486f88;
  color: #fff;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}

.form-error {
  margin: 8px 16px 0;
  color: #b42318;
  font-size: 12px;
  line-height: 1.35;
}

.send {
  width: calc(100% - 32px);
  margin: 14px 16px 16px;
  padding: 12px;
  border: none;
  border-radius: 16px;
  background:
      linear-gradient(
          180deg,
          rgba(80, 117, 153, 0.5),
          rgba(50, 97, 123, 0.6)
      );
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.send:hover {
  background:
      linear-gradient(
          180deg,
          rgba(80, 117, 153, 1),
          rgba(50, 97, 123, 1)
      );
}

.send:disabled {
  opacity: .5;
  cursor: not-allowed;
}

@media (max-width: 430px) {
  .feedback {
    right: 20px;
    bottom: 80px;
  }

  .chat {
    right: 20px;
    bottom: 90px;
    width: calc(100vw - 40px);
    max-height: calc(100vh - 110px);
  }
}
</style>
